import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "../lib/api";
import { PageHeader, StatusBadge } from "../components/ResourcePage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  user_id: "", employee_id: "", employee_name: "", period_start: "", period_end: "",
  hours: 0, gross_pay: 0, deductions: 0, status: "pending", pay_date: "",
};

export function Payroll() {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get("/payroll"), api.get("/staff-directory")]);
      setItems(p.data); setStaff(s.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(BLANK); setEditing(null); setOpen(true); };
  const openEdit = (row) => {
    setForm({
      user_id: row.user_id || "", employee_id: row.employee_id || "", employee_name: row.employee_name || "",
      period_start: row.period_start || "", period_end: row.period_end || "", hours: row.hours || 0,
      gross_pay: row.gross_pay || 0, deductions: row.deductions || 0, status: row.status || "pending",
      pay_date: row.pay_date || "",
    });
    setEditing(row.id); setOpen(true);
  };

  const pickEmployee = (val) => {
    const acc = staff.find((s) => s.user_id === val);
    setForm((f) => ({ ...f, user_id: val, employee_id: acc?.employee_id || "", employee_name: acc?.name || "" }));
  };

  const save = async () => {
    if (!form.employee_name) return toast.error("Select an employee.");
    if (!form.period_start || !form.period_end) return toast.error("Enter the pay period.");
    const payload = {
      ...form, hours: Number(form.hours || 0),
      gross_pay: Number(form.gross_pay || 0), deductions: Number(form.deductions || 0),
    };
    try {
      if (editing) await api.put(`/payroll/${editing}`, payload);
      else await api.post("/payroll", payload);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/payroll/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const net = Number(form.gross_pay || 0) - Number(form.deductions || 0);
  const selectValue = form.user_id || (form.employee_name ? "__legacy__" : "");

  return (
    <div className="fade-up">
      <PageHeader title="Payroll" subtitle="Run pay periods — employees are pulled from User Management so IDs & names stay consistent."
        action={<Button onClick={openNew} data-testid="pay-add"><Plus className="mr-1.5 h-4 w-4" />Add</Button>} />

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Employee ID</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead className="text-right">Hours</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Deductions</TableHead>
              <TableHead className="text-right">Net pay</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="py-12 text-center text-muted-foreground">No pay runs yet. Click Add to get started.</TableCell></TableRow>
            ) : items.map((row) => (
              <TableRow key={row.id} data-testid="pay-row">
                <TableCell className="font-semibold">{row.employee_name || "—"}</TableCell>
                <TableCell className="text-sm font-mono">{row.employee_id || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(row.period_start)}</TableCell>
                <TableCell className="text-sm">{fmtDate(row.period_end)}</TableCell>
                <TableCell className="text-right num">{row.hours ?? 0}</TableCell>
                <TableCell className="text-right num">{fmtMoney(row.gross_pay)}</TableCell>
                <TableCell className="text-right num">{fmtMoney(row.deductions)}</TableCell>
                <TableCell className="text-right num font-semibold">{fmtMoney(row.net_pay)}</TableCell>
                <TableCell><StatusBadge value={row.status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid="pay-edit"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelId(row.id)} data-testid="pay-delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit" : "New"} Pay run</DialogTitle>
            <DialogDescription>Select an employee — their ID and name come from User Management.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label className="text-xs">Employee</Label>
              <Select value={selectValue} onValueChange={pickEmployee}>
                <SelectTrigger className="mt-1.5" data-testid="pay-field-employee"><SelectValue placeholder="Select an employee" /></SelectTrigger>
                <SelectContent>
                  {selectValue === "__legacy__" && <SelectItem value="__legacy__" disabled>{form.employee_name} (legacy entry)</SelectItem>}
                  {staff.map((s) => (
                    <SelectItem key={s.user_id} value={s.user_id}>
                      {s.name} {s.employee_id ? `· ${s.employee_id}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.employee_id && <p className="mt-1.5 text-xs text-muted-foreground">Employee ID: <b className="font-mono">{form.employee_id}</b></p>}
            </div>
            <div>
              <Label className="text-xs">Period start</Label>
              <Input className="mt-1.5" type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="pay-field-start" />
            </div>
            <div>
              <Label className="text-xs">Period end</Label>
              <Input className="mt-1.5" type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="pay-field-end" />
            </div>
            <div>
              <Label className="text-xs">Hours worked</Label>
              <Input className="mt-1.5" type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="pay-field-hours" />
            </div>
            <div>
              <Label className="text-xs">Gross pay (₦)</Label>
              <Input className="mt-1.5" type="number" value={form.gross_pay} onChange={(e) => setForm({ ...form, gross_pay: e.target.value })} data-testid="pay-field-gross" />
            </div>
            <div>
              <Label className="text-xs">Deductions (₦)</Label>
              <Input className="mt-1.5" type="number" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} data-testid="pay-field-deductions" />
            </div>
            <div>
              <Label className="text-xs">Net pay</Label>
              <Input className="mt-1.5" value={fmtMoney(net)} disabled data-testid="pay-field-net" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="pay-field-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Pay date</Label>
              <Input className="mt-1.5" type="date" value={form.pay_date} onChange={(e) => setForm({ ...form, pay_date: e.target.value })} data-testid="pay-field-paydate" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="pay-save">{editing ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pay run?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive" data-testid="pay-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Payroll;
